// Dialog — richer modal than AlertDialog (allows custom content, close button).
// Use for "show sensitive value once" flows (webhook URL, api key).

import * as React from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

const Dialog = DialogPrimitive.Root
const DialogTrigger = DialogPrimitive.Trigger
const DialogPortal = DialogPrimitive.Portal
const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/40 backdrop-blur-sm',
      'data-[state=open]:animate-in data-[state=open]:fade-in-0',
      'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      data-slot="dialog"
      className={cn(
        // Dưới sm: bottom sheet — dính đáy màn hình, bo góc trên, tôn trọng safe-area.
        // Từ sm trở lên: dialog căn giữa như trước.
        // grid-cols-1 (minmax(0,1fr)): một con rộng (bảng min-w, vùng cuộn ngang) không được nới cột ra quá
        // khung — không có thì cả hộp rộng theo bảng và bị cắt bên phải trên điện thoại.
        'fixed inset-x-0 bottom-0 sm:left-1/2 sm:top-1/2 sm:bottom-auto z-50 grid grid-cols-1 w-full sm:w-full sm:max-w-lg gap-4 border-t sm:border border-rule bg-raise p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-6 shadow-overlay rounded-t-2xl sm:rounded-lg max-h-[92dvh] sm:max-h-[85dvh] overflow-y-auto sm:-translate-x-1/2 sm:-translate-y-1/2',
        'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom sm:data-[state=open]:zoom-in-95 sm:data-[state=open]:slide-in-from-bottom-0',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom sm:data-[state=closed]:zoom-out-95 sm:data-[state=closed]:slide-out-to-bottom-0',
        className,
      )}
      {...props}
    >
      <div className="mx-auto -mt-1 mb-1 h-1 w-10 shrink-0 rounded-full bg-rule sm:hidden" />
      {children}
      <DialogPrimitive.Close
        className="absolute right-2 top-2 size-10 sm:size-9 flex items-center justify-center rounded-lg text-muted opacity-70 transition-opacity hover:opacity-100 hover:bg-paperSoft focus:outline-none focus:ring-2 focus:ring-accent"
        aria-label="Đóng"
      >
        <X className="size-4" />
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-1.5 text-left', className)} {...props} />
)

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)} {...props} />
)

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(' text-xl leading-tight tracking-tight text-ink', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn('text-sm text-inkSoft', className)} {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogClose,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
